/**
 * Display formatting.
 *
 * Currency and quantities render with fixed fraction digits so a column of
 * numbers stays aligned. Pair with the DataTable's `numeric` column flag, which
 * applies tabular figures.
 */

const CURRENCY = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const QUANTITY = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

const RATE = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

/** Formats money, rendering a missing value as an em dash rather than zero. */
export function money(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return CURRENCY.format(value);
}

/** Formats money with an explicit sign, for variances. */
export function signedMoney(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const formatted = CURRENCY.format(Math.abs(value));
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
}

/** Formats a whole-unit quantity. */
export function quantity(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return QUANTITY.format(value);
}

/** Formats a per-unit rate at four decimal places. */
export function rate(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return RATE.format(value);
}

/** Turns a snake_case code into readable text: `partial_receipt` -> `Partial receipt`. */
export function humanise(code: string): string {
  const spaced = code.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
